import type { VerifyResponse } from './types';
import type { ApiDomainDeps } from './domainDeps';

export function createAuthDomain(deps: ApiDomainDeps) {
  const getWebRedirectUri = (): string | undefined => {
    if (typeof window === 'undefined' || !window.location?.origin) return undefined;
    return `${window.location.origin}/auth/verify`;
  };

  return {
    async login(email: string): Promise<void> {
      const redirectUri = getWebRedirectUri();
      const response = await fetch(`${deps.apiBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ...(redirectUri ? { redirectUri } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to send magic link' }));
        throw new Error(error.error || 'Failed to send magic link');
      }
    },

    async verifyToken(token: string, email: string): Promise<VerifyResponse> {
      const response = await fetch(`${deps.apiBaseUrl}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, email }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Invalid or expired token' }));
        throw new Error(error.error || 'Invalid or expired token');
      }

      const data = await response.json();

      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      const responseAny = data as Record<string, unknown>;
      if (responseAny.token) {
        localStorage.setItem('token', String(responseAny.token));
      }
      if (responseAny.jwt_token) {
        localStorage.setItem('jwt_token', String(responseAny.jwt_token));
      }
      if (responseAny.idToken) {
        localStorage.setItem('idToken', String(responseAny.idToken));
      }

      if (data.accessToken && data.refreshToken) {
        deps.tokenManager.onTokensUpdated();
      } else {
        deps.logger.warn('verifyToken: Missing tokens in response', {
          hasAccessToken: !!data.accessToken,
          hasRefreshToken: !!data.refreshToken,
        });
      }

      return data;
    },

    async loginWithGoogle(idToken: string): Promise<VerifyResponse> {
      const response = await fetch(`${deps.apiBaseUrl}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Google login failed' }));
        throw new Error(error.error || error.message || 'Google login failed');
      }

      const data = await response.json();

      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      if (data.email) {
        localStorage.setItem('email', data.email);
      }
      if (data.subscriptionPlan) {
        localStorage.setItem('subscriptionPlan', data.subscriptionPlan);
      }

      if (data.accessToken && data.refreshToken) {
        deps.tokenManager.onTokensUpdated();
      } else {
        deps.logger.warn('loginWithGoogle: Missing tokens in response', {
          hasAccessToken: !!data.accessToken,
          hasRefreshToken: !!data.refreshToken,
        });
      }

      return data;
    },

    async refreshAccessToken(): Promise<string> {
      await deps.tokenManager.forceRefresh();
      const token = await deps.tokenManager.getAccessToken();
      if (!token) {
        throw new Error('No access token available after refresh');
      }
      return token;
    },

    async logout(): Promise<void> {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          await fetch(`${deps.apiBaseUrl}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });
        } catch (error) {
          deps.logger.error('Logout error', error);
        }
      }

      deps.tokenManager.clearTokens();
    },

    isAuthenticated(): boolean {
      return !!localStorage.getItem('accessToken');
    },

    async getMe(): Promise<VerifyResponse> {
      const raw = await deps.request<VerifyResponse & { user?: VerifyResponse }>('/api/auth/me');
      return (raw && typeof raw === 'object' && raw.user && typeof raw.user === 'object') ? raw.user : raw;
    },
  };
}
